import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, ShieldCheck, MapPin } from "lucide-react";
import { PLANS, planToParams } from "@/data/content";

const STATS = [
  { icon: BadgeCheck, label: "One-time fee", sub: "Pay once per project" },
  { icon: ShieldCheck, label: "15-day free-fix", sub: "We fix issues free" },
  { icon: MapPin, label: "India + USA", sub: "We work across both" },
];

export default function Hero() {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-[#26395F]" data-testid="hero-section">
      {/* Decorative gradient glow, no imagery */}
      <div className="absolute -top-24 -right-24 h-[28rem] w-[28rem] rounded-full bg-[#3B82F6]/25 blur-[120px]" />
      <div className="absolute -bottom-32 -left-20 h-[24rem] w-[24rem] rounded-full bg-[#3B82F6]/10 blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-[#60A5FA] mb-6 uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-[#60A5FA] animate-pulse" /> Independent Consultancy
          </span>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.02] text-white">
            Radio, app &amp; software consultancy, <span className="text-[#60A5FA]">done right.</span>
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
            Radio App Consultancy is an independent consultancy. We help you with radio setup &amp; guidance, build
            websites, mobile apps and custom software, and set you up with genuine antivirus — all for a{" "}
            <span className="text-white font-semibold">one-time charge</span> — no subscription, ever.
            If something isn't working after delivery, we fix it free within 15 days.
          </p>
          <p className="mt-4 text-sm text-white/55 max-w-xl leading-relaxed" data-testid="hero-radio-line">
            A leading independent service provider for radio — we independently provide the service.
            We are not affiliated with, endorsed by, or sponsored by any brand.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={() => navigate(`/checkout${planToParams(PLANS[0])}`)}
              className="btn-sweep bg-[#3B82F6] text-white font-semibold px-8 py-4 rounded-xl hover:bg-[#2563EB] transition-colors duration-300"
              data-testid="hero-trial-btn"
            >
              Start your project
            </button>
            <a href="#plans" className="border border-white/25 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/10 transition-colors" data-testid="hero-choose-plan-btn">
              See services
            </a>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            {STATS.map((s) => (
              <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm px-5 py-4 flex items-center gap-3">
                <span className="h-10 w-10 rounded-lg bg-[#3B82F6]/20 flex items-center justify-center shrink-0">
                  <s.icon className="h-5 w-5 text-[#60A5FA]" />
                </span>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">{s.label}</p>
                  <p className="text-white/50 text-xs">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function PlanNav() { return null; }
export { PLANS, planToParams };
